import * as React from 'react';
import PageTitle from '@components/pageTitle';
import './style.css';
import dayjs from 'dayjs';
import { getFinancialJournalDetails } from '@pages/posTerminal/sideMenu/api';
import translate from '@data/translations';
import InlineRangeInput from '@components/inlineRangeInput';
import {
    InvoiceConclusionDetailsHeader,
    InvoiceConclusionDetailsList,
    SelectedTypeOrSubTypeByPosTerminalId,
} from '@pages/posTerminal/sideMenu/invoiceConclusion';
import { startGlobalLoading, stopGlobalLoading } from '@components/loaders/globalLoading';
import { InvoiceConclusionDetailsByPosTerminalId } from '../../common/modelDefinition';
import useStore from '@data/state/zustand';
import { getDaysBetween } from '@pages/generalLedger/glCards/accountCards';
import { toast } from 'react-toastify';
import { ReactComponent as Print } from '../../assets/custom/icons/printer.svg';
import { ReactComponent as Search } from '../../assets/custom/icons/search.svg';
import SplitButton from '@components/splitButton';
import { SplitButtonOption } from '@components/splitButton/optionInterface';
import CustomInput from '@components/custom/input';

const FinancialJournal = (props: any) => {
    const [invoiceConclusionDetailsByPosTerminalId, setInvoiceConclusionDetailsByPosTerminalId] =
        React.useState<InvoiceConclusionDetailsByPosTerminalId | null>(null);
    const [invoiceConclusionDetailsByShiftDate, setInvoiceConclusionDetailsByShiftDate] = React.useState<{
        [shiftDateTimestamp: string]: InvoiceConclusionDetailsByPosTerminalId;
    } | null>(null);
    const [groupByShiftDate, setGroupByShiftDate] = React.useState(false);
    const [from, setFromDate] = React.useState(null as number | null);
    const [to, setToDate] = React.useState(null as number | null);
    const [year, setYear] = React.useState(new Date().getFullYear() as number | null);
    const [month, setMonth] = React.useState(null as number | null);

    const [fromRange, setFromRangeDate] = React.useState(new Date().getTime() as number | null);
    const [toRange, setToRangeDate] = React.useState(new Date().getTime() as number | null);
    const [reloadDataAfterRangeChange, setReloadDataAfterRangeChange] = React.useState(false as boolean);
    const [invoiceConclusionDetailsByPluByPosTerminalId, setInvoiceConclusionsDetailsByPluByPosTerminalId] =
        React.useState<InvoiceConclusionDetailsByPosTerminalId | null>(null);
    const [invoiceConclusionDetailsByPluByShiftDate, setInvoiceConclusionDetailsByPluByShiftDate] = React.useState<{
        [shiftDateTimestamp: string]: InvoiceConclusionDetailsByPosTerminalId;
    } | null>(null);
    const [selectedTypeOrSubType, setSelectedTypeOrSubType] =
        React.useState<SelectedTypeOrSubTypeByPosTerminalId | null>(null);
    const [searchTerm, setSearchTerm] = React.useState('');

    const viewModeOptions: SplitButtonOption[] = [
        { value: 'default', label: translate('Default') },
        { value: 'byShiftDate', label: translate('By shift date') },
    ];
    const viewMode = groupByShiftDate ? viewModeOptions[1] : viewModeOptions[0];

    const systemLocale: string = useStore((appState: any) => appState.locale);
    const defaultCurrency: string = useStore((appState: any) => appState.defaultCurrency);
    const otherSettings: any = useStore((appState: any) => appState.otherSettings);

    const [shouldPrint, setShouldPrint] = React.useState(false);
    const originalInvoiceConclusionDetailsRef = React.useRef<any>(null);
    const originalInvoiceConclusionsDetailsByPluRef = React.useRef<any>(null);
    const prevSearchTermRef = React.useRef('');

    // Filter helper function
    const filterBySearchTerm = (items: any[] | undefined, term: string) => {
        if (!items || !term.trim()) return items;
        const lowerTerm = term.toLowerCase();
        return items.filter((item: any) => item.name?.toLowerCase().includes(lowerTerm));
    };

    // Filtered data for default mode (by terminal)
    const filteredByPosTerminalId = React.useMemo(() => {
        if (!searchTerm.trim() || !invoiceConclusionDetailsByPluByPosTerminalId) {
            return {
                type: invoiceConclusionDetailsByPosTerminalId,
                plu: invoiceConclusionDetailsByPluByPosTerminalId,
            };
        }

        const filteredPlu: InvoiceConclusionDetailsByPosTerminalId = {};
        const filteredType: InvoiceConclusionDetailsByPosTerminalId = {};
        const matchedTypesPerTerminal: { [terminalName: string]: Set<string> } = {};

        // First pass: filter PLU data and collect matched types/subtypes
        Object.entries(invoiceConclusionDetailsByPluByPosTerminalId).forEach(
            ([terminalName, terminalData]: [string, any]) => {
                const filteredInvoiced = filterBySearchTerm(terminalData.invoiced, searchTerm);

                if (filteredInvoiced && filteredInvoiced.length > 0) {
                    filteredPlu[terminalName] = {
                        ...terminalData,
                        invoiced: filteredInvoiced,
                    };

                    // Collect matched types and subtypes
                    matchedTypesPerTerminal[terminalName] = new Set();
                    filteredInvoiced.forEach((item: any) => {
                        if (item.type) matchedTypesPerTerminal[terminalName].add(item.type);
                        if (item.subtype) matchedTypesPerTerminal[terminalName].add(item.subtype);
                    });
                }
            }
        );

        // Second pass: filter TYPE data to show only types with matched items
        if (invoiceConclusionDetailsByPosTerminalId) {
            Object.entries(invoiceConclusionDetailsByPosTerminalId).forEach(
                ([terminalName, terminalData]: [string, any]) => {
                    if (matchedTypesPerTerminal[terminalName]) {
                        const matchedTypes = matchedTypesPerTerminal[terminalName];
                        const filteredInvoiced = terminalData.invoiced?.filter((item: any) => {
                            // Check if this type/subtype has matched items
                            if (item.type && matchedTypes.has(item.type)) {
                                return true;
                            }
                            if (item.subtype && matchedTypes.has(item.subtype)) {
                                return true;
                            }
                            return false;
                        });

                        if (filteredInvoiced && filteredInvoiced.length > 0) {
                            filteredType[terminalName] = {
                                ...terminalData,
                                invoiced: filteredInvoiced,
                            };
                        }
                    }
                }
            );
        }

        return {
            type: Object.keys(filteredType).length > 0 ? filteredType : null,
            plu: Object.keys(filteredPlu).length > 0 ? filteredPlu : null,
        };
    }, [searchTerm, invoiceConclusionDetailsByPosTerminalId, invoiceConclusionDetailsByPluByPosTerminalId]);

    // Filtered data for groupByShiftDate mode
    const filteredByShiftDate = React.useMemo(() => {
        if (!searchTerm.trim() || !invoiceConclusionDetailsByPluByShiftDate) {
            return {
                type: invoiceConclusionDetailsByShiftDate,
                plu: invoiceConclusionDetailsByPluByShiftDate,
            };
        }

        const filteredPlu: { [timestamp: string]: InvoiceConclusionDetailsByPosTerminalId } = {};
        const filteredType: { [timestamp: string]: InvoiceConclusionDetailsByPosTerminalId } = {};
        const matchedTypesPerDateTerminal: { [key: string]: Set<string> } = {};

        // First pass: filter PLU data
        Object.entries(invoiceConclusionDetailsByPluByShiftDate).forEach(
            ([dateKey, terminalsByDate]: [string, any]) => {
                const filteredTerminals: InvoiceConclusionDetailsByPosTerminalId = {};

                Object.entries(terminalsByDate).forEach(([terminalName, terminalData]: [string, any]) => {
                    const filteredInvoiced = filterBySearchTerm(terminalData.invoiced, searchTerm);

                    if (filteredInvoiced && filteredInvoiced.length > 0) {
                        filteredTerminals[terminalName] = {
                            ...terminalData,
                            invoiced: filteredInvoiced,
                        };

                        const key = `${dateKey}-${terminalName}`;
                        matchedTypesPerDateTerminal[key] = new Set();
                        filteredInvoiced.forEach((item: any) => {
                            if (item.type) matchedTypesPerDateTerminal[key].add(item.type);
                            if (item.subtype) matchedTypesPerDateTerminal[key].add(item.subtype);
                        });
                    }
                });

                if (Object.keys(filteredTerminals).length > 0) {
                    filteredPlu[dateKey] = filteredTerminals;
                }
            }
        );

        // Second pass: filter TYPE data
        if (invoiceConclusionDetailsByShiftDate) {
            Object.entries(invoiceConclusionDetailsByShiftDate).forEach(([dateKey, terminalsByDate]: [string, any]) => {
                const filteredTerminals: InvoiceConclusionDetailsByPosTerminalId = {};

                Object.entries(terminalsByDate).forEach(([terminalName, terminalData]: [string, any]) => {
                    const key = `${dateKey}-${terminalName}`;
                    if (matchedTypesPerDateTerminal[key]) {
                        const matchedTypes = matchedTypesPerDateTerminal[key];
                        const filteredInvoiced = terminalData.invoiced?.filter((item: any) => {
                            if (item.type && matchedTypes.has(item.type)) {
                                return true;
                            }
                            if (item.subtype && matchedTypes.has(item.subtype)) {
                                return true;
                            }
                            return false;
                        });

                        if (filteredInvoiced && filteredInvoiced.length > 0) {
                            filteredTerminals[terminalName] = {
                                ...terminalData,
                                invoiced: filteredInvoiced,
                            };
                        }
                    }
                });

                if (Object.keys(filteredTerminals).length > 0) {
                    filteredType[dateKey] = filteredTerminals;
                }
            });
        }

        return {
            type: Object.keys(filteredType).length > 0 ? filteredType : null,
            plu: Object.keys(filteredPlu).length > 0 ? filteredPlu : null,
        };
    }, [searchTerm, invoiceConclusionDetailsByShiftDate, invoiceConclusionDetailsByPluByShiftDate]);

    React.useEffect(() => {
        const loadData = async () => {
            startGlobalLoading();
            const _invoiceConclusionsDetails = await getFinancialJournalDetails({
                group: 'type',
                from: from
                    ? new Date(from).setHours(12)
                    : fromRange
                    ? new Date(fromRange).setHours(12)
                    : new Date().setHours(12),
                to: to ? new Date(to).setHours(12) : toRange ? new Date(toRange).setHours(12) : new Date().setHours(12),
                groupByShiftDate,
            });
            stopGlobalLoading();

            if (_invoiceConclusionsDetails) {
                setInvoiceConclusionsDetailsByPluByPosTerminalId(null);
                setInvoiceConclusionDetailsByPluByShiftDate(null);
                setSelectedTypeOrSubType(null);
                if (groupByShiftDate) {
                    setInvoiceConclusionDetailsByShiftDate(_invoiceConclusionsDetails);
                    setInvoiceConclusionDetailsByPosTerminalId(null);
                } else {
                    setInvoiceConclusionDetailsByPosTerminalId(_invoiceConclusionsDetails);
                    setInvoiceConclusionDetailsByShiftDate(null);
                }
            }
        };

        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [from, to, year, month, reloadDataAfterRangeChange, groupByShiftDate]);

    React.useEffect(() => {
        const loadData = async () => {
            const pluDataExists = groupByShiftDate
                ? invoiceConclusionDetailsByPluByShiftDate
                : invoiceConclusionDetailsByPluByPosTerminalId;

            if (selectedTypeOrSubType && !pluDataExists) {
                startGlobalLoading();
                const _invoiceConclusionsDetails = await getFinancialJournalDetails({
                    group: 'plu',
                    from: from
                        ? new Date(from).setHours(12)
                        : fromRange
                        ? new Date(fromRange).setHours(12)
                        : new Date().setHours(12),
                    to: to
                        ? new Date(to).setHours(12)
                        : toRange
                        ? new Date(toRange).setHours(12)
                        : new Date().setHours(12),
                    groupByShiftDate,
                });
                stopGlobalLoading();

                if (_invoiceConclusionsDetails) {
                    if (groupByShiftDate) {
                        setInvoiceConclusionDetailsByPluByShiftDate(_invoiceConclusionsDetails);
                    } else {
                        setInvoiceConclusionsDetailsByPluByPosTerminalId(_invoiceConclusionsDetails);
                    }
                }
            }
        };
        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [
        selectedTypeOrSubType,
        invoiceConclusionDetailsByPluByPosTerminalId,
        invoiceConclusionDetailsByPluByShiftDate,
        from,
        to,
        reloadDataAfterRangeChange,
        groupByShiftDate,
    ]);

    // Auto-fetch PLU data when user starts searching
    React.useEffect(() => {
        const loadPluDataForSearch = async () => {
            if (!searchTerm.trim()) return;

            const pluDataExists = groupByShiftDate
                ? invoiceConclusionDetailsByPluByShiftDate
                : invoiceConclusionDetailsByPluByPosTerminalId;

            if (!pluDataExists) {
                startGlobalLoading();
                const _invoiceConclusionsDetails = await getFinancialJournalDetails({
                    group: 'plu',
                    from: from
                        ? new Date(from).setHours(12)
                        : fromRange
                        ? new Date(fromRange).setHours(12)
                        : new Date().setHours(12),
                    to: to
                        ? new Date(to).setHours(12)
                        : toRange
                        ? new Date(toRange).setHours(12)
                        : new Date().setHours(12),
                    groupByShiftDate,
                });
                stopGlobalLoading();

                if (_invoiceConclusionsDetails) {
                    if (groupByShiftDate) {
                        setInvoiceConclusionDetailsByPluByShiftDate(_invoiceConclusionsDetails);
                    } else {
                        setInvoiceConclusionsDetailsByPluByPosTerminalId(_invoiceConclusionsDetails);
                    }
                }
            }
        };

        // Debounce - čekaj 300ms nakon što korisnik prestane tipkati
        const timeoutId = setTimeout(() => {
            loadPluDataForSearch().catch((err) => {
                console.log(err);
            });
        }, 300);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line
    }, [
        searchTerm,
        groupByShiftDate,
        invoiceConclusionDetailsByPluByShiftDate,
        invoiceConclusionDetailsByPluByPosTerminalId,
    ]);

    // Auto-expand types when searching
    React.useEffect(() => {
        const prevSearchTerm = prevSearchTermRef.current;
        prevSearchTermRef.current = searchTerm;

        if (!searchTerm.trim()) {
            // Only clear selection if user actively cleared the search (was typing before)
            if (prevSearchTerm.trim()) {
                setSelectedTypeOrSubType(null);
            }
            return;
        }

        const pluData = groupByShiftDate ? filteredByShiftDate.plu : filteredByPosTerminalId.plu;
        if (!pluData) return;

        const autoExpandTypes: SelectedTypeOrSubTypeByPosTerminalId = {};

        if (groupByShiftDate) {
            // For groupByShiftDate mode
            Object.values(pluData as { [key: string]: InvoiceConclusionDetailsByPosTerminalId }).forEach(
                (terminalsByDate) => {
                    Object.entries(terminalsByDate).forEach(([terminalName, terminalData]: [string, any]) => {
                        if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                            const posTerminalId = terminalData.invoiced[0].posTerminalId;
                            if (!autoExpandTypes[posTerminalId]) {
                                autoExpandTypes[posTerminalId] = [];
                            }
                            terminalData.invoiced.forEach((item: any) => {
                                if (item.type && !autoExpandTypes[posTerminalId].includes(item.type)) {
                                    autoExpandTypes[posTerminalId].push(item.type);
                                }
                                if (item.subtype && !autoExpandTypes[posTerminalId].includes(item.subtype)) {
                                    autoExpandTypes[posTerminalId].push(item.subtype);
                                }
                            });
                        }
                    });
                }
            );
        } else {
            // For default mode
            Object.entries(pluData as InvoiceConclusionDetailsByPosTerminalId).forEach(
                ([terminalName, terminalData]: [string, any]) => {
                    if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                        const posTerminalId = terminalData.invoiced[0].posTerminalId;
                        if (!autoExpandTypes[posTerminalId]) {
                            autoExpandTypes[posTerminalId] = [];
                        }
                        terminalData.invoiced.forEach((item: any) => {
                            if (item.type && !autoExpandTypes[posTerminalId].includes(item.type)) {
                                autoExpandTypes[posTerminalId].push(item.type);
                            }
                            if (item.subtype && !autoExpandTypes[posTerminalId].includes(item.subtype)) {
                                autoExpandTypes[posTerminalId].push(item.subtype);
                            }
                        });
                    }
                }
            );
        }

        if (Object.keys(autoExpandTypes).length > 0) {
            setSelectedTypeOrSubType(autoExpandTypes);
        }
        // eslint-disable-next-line
    }, [searchTerm, filteredByShiftDate.plu, filteredByPosTerminalId.plu, groupByShiftDate]);

    React.useEffect(() => {
        if (shouldPrint) {
            window.print();
            // Vrati originalne podatke nakon printa
            setTimeout(() => {
                if (groupByShiftDate) {
                    // Vraćanje originalnih podataka za groupByShiftDate mod
                    if (originalInvoiceConclusionDetailsRef.current) {
                        setInvoiceConclusionDetailsByShiftDate(originalInvoiceConclusionDetailsRef.current);
                        originalInvoiceConclusionDetailsRef.current = null;
                    }
                    if (originalInvoiceConclusionsDetailsByPluRef.current) {
                        setInvoiceConclusionDetailsByPluByShiftDate(originalInvoiceConclusionsDetailsByPluRef.current);
                        originalInvoiceConclusionsDetailsByPluRef.current = null;
                    }
                } else {
                    // Vraćanje originalnih podataka za default mod
                    if (originalInvoiceConclusionDetailsRef.current) {
                        setInvoiceConclusionDetailsByPosTerminalId(originalInvoiceConclusionDetailsRef.current);
                        originalInvoiceConclusionDetailsRef.current = null;
                    }
                    if (originalInvoiceConclusionsDetailsByPluRef.current) {
                        setInvoiceConclusionsDetailsByPluByPosTerminalId(
                            originalInvoiceConclusionsDetailsByPluRef.current
                        );
                        originalInvoiceConclusionsDetailsByPluRef.current = null;
                    }
                }

                setShouldPrint(false); // resetiraj flag
            }, 100);
        }
        // eslint-disable-next-line
    }, [shouldPrint, groupByShiftDate]);

    const handlePrintClick = async (selectedTypeOrSubType?: any) => {
        // Za groupByShiftDate mod
        if (groupByShiftDate) {
            let dataToUse = invoiceConclusionDetailsByPluByShiftDate;

            if (!dataToUse) {
                startGlobalLoading();
                const fetchedData = await getFinancialJournalDetails({
                    group: 'plu',
                    from: from
                        ? new Date(from).setHours(12)
                        : fromRange
                        ? new Date(fromRange).setHours(12)
                        : new Date().setHours(12),
                    to: to
                        ? new Date(to).setHours(12)
                        : toRange
                        ? new Date(toRange).setHours(12)
                        : new Date().setHours(12),
                    groupByShiftDate: true,
                });
                stopGlobalLoading();

                if (fetchedData) {
                    setInvoiceConclusionDetailsByPluByShiftDate(fetchedData);
                    dataToUse = fetchedData;
                }
            }

            if (dataToUse) {
                if (selectedTypeOrSubType) {
                    // Filtriranje po tipu za groupByShiftDate mod
                    originalInvoiceConclusionDetailsRef.current = invoiceConclusionDetailsByShiftDate
                        ? JSON.parse(JSON.stringify(invoiceConclusionDetailsByShiftDate))
                        : null;
                    originalInvoiceConclusionsDetailsByPluRef.current = invoiceConclusionDetailsByPluByShiftDate
                        ? JSON.parse(JSON.stringify(invoiceConclusionDetailsByPluByShiftDate))
                        : null;

                    // Filtriraj PLU podatke
                    const filteredPluData: { [shiftDateTimestamp: string]: InvoiceConclusionDetailsByPosTerminalId } =
                        {};

                    Object.entries(dataToUse).forEach(([dateKey, terminalsByDate]: [string, any]) => {
                        const filteredTerminals: any = {};

                        Object.entries(terminalsByDate).forEach(([terminalName, terminalData]: any) => {
                            if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                                const posTerminalId = terminalData.invoiced[0].posTerminalId;

                                if (selectedTypeOrSubType[posTerminalId]) {
                                    const allowedTypes = selectedTypeOrSubType[posTerminalId];

                                    const filteredInvoiced = terminalData.invoiced.filter((item: any) => {
                                        const itemType = item.type;
                                        const itemSubType = item.subtype;

                                        return (
                                            allowedTypes.includes(itemSubType) ||
                                            allowedTypes.includes(itemType) ||
                                            (itemSubType === null && allowedTypes.includes('null')) ||
                                            (itemType === null && allowedTypes.includes('null'))
                                        );
                                    });

                                    if (filteredInvoiced.length > 0) {
                                        filteredTerminals[terminalName] = {
                                            ...terminalData,
                                            invoiced: filteredInvoiced,
                                        };
                                    }
                                }
                            }
                        });

                        if (Object.keys(filteredTerminals).length > 0) {
                            filteredPluData[dateKey] = filteredTerminals;
                        }
                    });

                    // Filtriraj i glavne (type) podatke
                    if (invoiceConclusionDetailsByShiftDate) {
                        const filteredMainData: {
                            [shiftDateTimestamp: string]: InvoiceConclusionDetailsByPosTerminalId;
                        } = {};

                        Object.entries(invoiceConclusionDetailsByShiftDate).forEach(
                            ([dateKey, terminalsByDate]: [string, any]) => {
                                const filteredTerminals: any = {};

                                Object.entries(terminalsByDate).forEach(([terminalName, terminalData]: any) => {
                                    if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                                        const posTerminalId = terminalData.invoiced[0].posTerminalId;

                                        if (selectedTypeOrSubType[posTerminalId]) {
                                            const allowedTypes = selectedTypeOrSubType[posTerminalId];

                                            const filteredMainInvoiced = terminalData.invoiced.filter((item: any) => {
                                                const itemType = item.type;
                                                const itemSubType = item.subtype;

                                                return (
                                                    allowedTypes.includes(itemSubType) ||
                                                    allowedTypes.includes(itemType) ||
                                                    (itemSubType === null &&
                                                        allowedTypes.includes('null') &&
                                                        itemType === null &&
                                                        allowedTypes.includes('null'))
                                                );
                                            });

                                            if (filteredMainInvoiced.length > 0) {
                                                filteredTerminals[terminalName] = {
                                                    ...terminalData,
                                                    invoiced: filteredMainInvoiced,
                                                };
                                            }
                                        }
                                    }
                                });

                                if (Object.keys(filteredTerminals).length > 0) {
                                    filteredMainData[dateKey] = filteredTerminals;
                                }
                            }
                        );

                        setInvoiceConclusionDetailsByShiftDate(filteredMainData);
                    }

                    setInvoiceConclusionDetailsByPluByShiftDate(filteredPluData);
                } else {
                    // Print all - skupljamo sve tipove iz svih datuma i terminala
                    const allTypesByTerminal: SelectedTypeOrSubTypeByPosTerminalId = {};

                    Object.values(dataToUse).forEach((terminalsByDate: InvoiceConclusionDetailsByPosTerminalId) => {
                        Object.entries(terminalsByDate).forEach(([terminalName, terminalData]: any) => {
                            const terminalTypes: string[] = [];

                            if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                                const posTerminalId = terminalData.invoiced[0].posTerminalId;

                                terminalData.invoiced.forEach((item: any) => {
                                    if (item.type !== undefined && !terminalTypes.includes(String(item.type))) {
                                        terminalTypes.push(String(item.type));
                                    }
                                    if (item.subtype !== undefined && !terminalTypes.includes(String(item.subtype))) {
                                        terminalTypes.push(String(item.subtype));
                                    }
                                });

                                if (!allTypesByTerminal[posTerminalId]) {
                                    allTypesByTerminal[posTerminalId] = [];
                                }
                                terminalTypes.forEach((t) => {
                                    if (!allTypesByTerminal[posTerminalId].includes(t)) {
                                        allTypesByTerminal[posTerminalId].push(t);
                                    }
                                });
                            }
                        });
                    });

                    setSelectedTypeOrSubType(allTypesByTerminal);
                }
            }
            setShouldPrint(true);
            return;
        }

        // Originalna logika za default mod (bez groupByShiftDate)
        let dataToUse = invoiceConclusionDetailsByPluByPosTerminalId;

        // Ako nemamo podatke, onda ih dohvatimo
        if (!dataToUse) {
            startGlobalLoading();
            const fetchedData = await getFinancialJournalDetails({
                group: 'plu',
                from: from
                    ? new Date(from).setHours(12)
                    : fromRange
                    ? new Date(fromRange).setHours(12)
                    : new Date().setHours(12),
                to: to ? new Date(to).setHours(12) : toRange ? new Date(toRange).setHours(12) : new Date().setHours(12),
                groupByShiftDate: false,
            });
            stopGlobalLoading();

            if (fetchedData) {
                setInvoiceConclusionsDetailsByPluByPosTerminalId(fetchedData);
                dataToUse = fetchedData;
            }
        }
        // Obradi podatke (bilo da su već bili u stateu ili smo ih upravo dobili)
        if (dataToUse) {
            if (selectedTypeOrSubType) {
                originalInvoiceConclusionDetailsRef.current = { ...invoiceConclusionDetailsByPosTerminalId };
                originalInvoiceConclusionsDetailsByPluRef.current = { ...invoiceConclusionDetailsByPluByPosTerminalId };

                // Filtriranje dataToUse (PLU podaci) na osnovu selectedTypeOrSubType
                const filteredData: any = {};

                Object.entries(dataToUse).forEach(([terminalName, terminalData]: any) => {
                    if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                        const posTerminalId = terminalData.invoiced[0].posTerminalId;

                        if (selectedTypeOrSubType[posTerminalId]) {
                            const allowedTypes = selectedTypeOrSubType[posTerminalId];

                            const filteredInvoiced = terminalData.invoiced.filter((item: any) => {
                                const itemType = item.type; // Ostavimo originalnu vrijednost
                                const itemSubType = item.subtype; // Ostavimo originalnu vrijednost

                                // Provjera da li allowedTypes sadrži itemType ili itemSubType
                                // Uključujući i null vrijednosti
                                return (
                                    allowedTypes.includes(itemSubType) ||
                                    allowedTypes.includes(itemType) ||
                                    // Specijalni slučaj za null
                                    (itemSubType === null && allowedTypes.includes('null')) ||
                                    (itemType === null && allowedTypes.includes('null'))
                                );
                            });

                            if (filteredInvoiced.length > 0) {
                                filteredData[terminalName] = {
                                    ...terminalData,
                                    invoiced: filteredInvoiced,
                                };
                            }
                        }
                    }
                });

                // Sada filtriramo invoiceConclusionDetailsByPosTerminalId po subtype-u
                if (invoiceConclusionDetailsByPosTerminalId) {
                    const filteredMainData: any = {};

                    Object.entries(invoiceConclusionDetailsByPosTerminalId).forEach(
                        ([terminalName, terminalData]: any) => {
                            if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                                const posTerminalId = terminalData.invoiced[0].posTerminalId;

                                if (selectedTypeOrSubType[posTerminalId]) {
                                    const allowedTypes = selectedTypeOrSubType[posTerminalId];

                                    const filteredMainInvoiced = terminalData.invoiced.filter((item: any) => {
                                        const itemType = item.type; // Ostavimo original
                                        const itemSubType = item.subtype; // Ostavimo original

                                        return (
                                            allowedTypes.includes(itemSubType) ||
                                            allowedTypes.includes(itemType) ||
                                            (itemSubType === null &&
                                                allowedTypes.includes('null') &&
                                                itemType === null &&
                                                allowedTypes.includes('null'))
                                        );
                                    });

                                    if (filteredMainInvoiced.length > 0) {
                                        filteredMainData[terminalName] = {
                                            ...terminalData,
                                            invoiced: filteredMainInvoiced,
                                        };
                                    }
                                }
                            }
                        }
                    );

                    setInvoiceConclusionDetailsByPosTerminalId(filteredMainData);
                }

                setInvoiceConclusionsDetailsByPluByPosTerminalId(filteredData);
            } else {
                const allTypesByTerminal: SelectedTypeOrSubTypeByPosTerminalId = {};

                Object.entries(dataToUse).forEach(([terminalName, terminalData]: any) => {
                    const terminalTypes: string[] = [];

                    if (terminalData.invoiced && terminalData.invoiced.length > 0) {
                        // Uzmemo posTerminalId iz prvog artikla (svi artikli u terminalu bi trebali imati isti posTerminalId)
                        const posTerminalId = terminalData.invoiced[0].posTerminalId;

                        terminalData.invoiced.forEach((item: any) => {
                            if (item.type !== undefined && !terminalTypes.includes(String(item.type))) {
                                terminalTypes.push(String(item.type));
                            }
                            if (item.subtype !== undefined && !terminalTypes.includes(String(item.subtype))) {
                                terminalTypes.push(String(item.subtype));
                            }
                        });

                        allTypesByTerminal[posTerminalId] = terminalTypes;
                    }
                });

                setSelectedTypeOrSubType(allTypesByTerminal);
            }
        }
        setShouldPrint(true);
    };

    const YearSelect = (props: any) => {
        const { _year, setYear } = props;
        let yearOptions = [];
        for (let i = _year - 2; i <= new Date().getFullYear(); i++) {
            yearOptions.push(
                <option key={i} value={i}>
                    {i}
                </option>
            );
        }
        return (
            <select
                className="form-control form-control-sm px-1 w-auto  bg-white"
                value={year ? year : ''}
                onChange={(e: any) => {
                    setYear(Number(e.target.value));
                    if (month) {
                        const firstDay = getFirstDayOfMonth(Number(e.target.value), Number(month) - 1);
                        const lastDay = getLastDayOfMonth(Number(e.target.value), Number(month) - 1);
                        setFromDate(dayjs(firstDay).startOf('day').valueOf());
                        setToDate(dayjs(lastDay).endOf('day').valueOf());
                    }
                }}
            >
                {yearOptions}
            </select>
        );
    };

    const getFirstDayOfMonth = (year: number, month: number) => {
        return new Date(year, month, 1);
    };

    const getLastDayOfMonth = (year: number, month: number) => {
        return new Date(year, month + 1, 0);
    };

    const MonthSelect = (props: any) => {
        const { setMonth } = props;
        let monthOptions = [];
        for (let i = 1; i <= 12; i++) {
            monthOptions.push(
                <option key={i} value={i}>
                    {dayjs()
                        .month(i - 1)
                        .format('MMMM')}
                </option>
            );
        }
        return (
            <select
                className="form-control form-control-sm px-1 w-auto bg-white ml-16p mr-16p"
                value={month ? month : ''}
                onChange={(e: any) => {
                    if (e.target.value === 'byDate') {
                        setFromRangeDate(new Date().getTime());
                        setToRangeDate(new Date().getTime());
                        setYear(new Date().getFullYear());
                        setMonth(null);
                        setFromDate(null);
                        setToDate(null);
                    } else {
                        const firstDay = getFirstDayOfMonth(Number(year), Number(e.target.value) - 1);
                        const lastDay = getLastDayOfMonth(Number(year), Number(e.target.value) - 1);
                        setMonth(Number(e.target.value));
                        setFromDate(dayjs(firstDay).startOf('day').valueOf());
                        setToDate(dayjs(lastDay).endOf('day').valueOf());
                        setFromRangeDate(null);
                        setToRangeDate(null);
                    }
                }}
            >
                <option key={'byDate'} value="byDate">
                    {translate('By Date')}
                </option>
                {monthOptions}
            </select>
        );
    };

    const handleDateSelection = async (type: string, date: any) => {
        const _from = type === 'from' ? date : fromRange;
        const _to = type === 'to' ? date : toRange;
        setFromRangeDate(_from);
        setToRangeDate(_to);

        if (getDaysBetween(_from, _to) > 40) {
            toast(translate('Maximum date range is 40 days'), { type: toast.TYPE.ERROR });
        } else {
            setReloadDataAfterRangeChange(!reloadDataAfterRangeChange);
        }
    };

    const handlePrint = () => {
        setShouldPrint(true); // window.print();
    };

    const hasSelections = selectedTypeOrSubType && Object.values(selectedTypeOrSubType).some((arr) => arr.length > 0);

    return (
        <div className="mobile-page">
            <div>
                <div className="title-with-options display-flex space-between no-print">
                    <div className="">
                        <PageTitle classPaddingBottom={true} />
                        <small className="financial-journal-subtitle no-print" style={{ paddingLeft: '32px' }}>
                            {translate('Based on shift start')}
                        </small>
                    </div>
                    <div className="page-title-button">
                        <div
                            className="mr-20p  button-primary"
                            style={{ padding: 0, paddingRight: '8px' }}
                            onClick={() => handlePrintClick()}
                        >
                            <div className="print-finJournal">
                                <Print />
                            </div>
                            {translate('Print all')}
                        </div>
                    </div>
                </div>

                <div className="search-and-page-controls display-flex space-between align-center">
                    <div className="display-flex mt-12p flex-wrap align-items-center">
                        <div className="flex-center no-print mr-16p">
                            <YearSelect _year={new Date().getFullYear()} setYear={setYear} />
                            <MonthSelect setMonth={setMonth} />
                            {!month ? (
                                <InlineRangeInput
                                    initialFrom={fromRange ? new Date(fromRange) : new Date()}
                                    initialTo={toRange ? new Date(toRange) : new Date()}
                                    onChange={handleDateSelection}
                                />
                            ) : null}
                        </div>
                        <div className="flex-center no-print mr-16p">
                            <SplitButton
                                onSelect={(value: string | number) => {
                                    setGroupByShiftDate(value === 'byShiftDate');
                                }}
                                selectedItem={viewMode}
                                options={viewModeOptions}
                                styleProp={{ marginTop: 0 }}
                            />
                        </div>
                        <div className="flex-center no-print">
                            <CustomInput
                                className={'on-mouse-down-show'}
                                onInputChange={(e: any) => {
                                    setSearchTerm(e.target.value);
                                }}
                                value={searchTerm}
                                icon={<Search />}
                                placeholder={translate('Search')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <div className="col-12 mt-2 mb-3 no-print d-flex justify-content-center align-items-center">
                    <h5 className="mt-2 mb-2 pointer p-2">{<b>{translate('Invoiced').toUpperCase()}</b>} </h5>
                    {hasSelections ? (
                        <div className="print-dark pointer" onClick={() => handlePrintClick(selectedTypeOrSubType)}>
                            <Print />
                        </div>
                    ) : (
                        <div className="print-dark pointer" onClick={() => handlePrint()}>
                            <Print />
                        </div>
                    )}
                </div>
                {searchTerm.trim() &&
                ((groupByShiftDate && !filteredByShiftDate.type) ||
                    (!groupByShiftDate && !filteredByPosTerminalId.type)) ? (
                    // No results found
                    <div className="text-center mt-4">
                        <p>
                            {translate('No results found for')} "<b>{searchTerm}</b>"
                        </p>
                    </div>
                ) : groupByShiftDate &&
                  (searchTerm.trim() ? filteredByShiftDate.type : invoiceConclusionDetailsByShiftDate) ? (
                    // Group by shift date view
                    Object.keys(searchTerm.trim() ? filteredByShiftDate.type! : invoiceConclusionDetailsByShiftDate!)
                        .sort((a, b) => Number(a) - Number(b))
                        .map((shiftDateTimestamp: string) => {
                            const dataSource = searchTerm.trim()
                                ? filteredByShiftDate.type!
                                : invoiceConclusionDetailsByShiftDate!;
                            const pluSource = searchTerm.trim()
                                ? filteredByShiftDate.plu
                                : invoiceConclusionDetailsByPluByShiftDate;
                            const terminalsByDate = dataSource[shiftDateTimestamp];
                            const formattedDate = dayjs(Number(shiftDateTimestamp))
                                .locale(systemLocale ? systemLocale : 'en')
                                .format('ddd: MMM DD YYYY');

                            return (
                                <div key={shiftDateTimestamp} className="mb-3">
                                    <h5
                                        className="ml-16p mt-2 mb-2"
                                        style={{ borderBottom: '1px solid #ccc', paddingBottom: '6px' }}
                                    >
                                        {formattedDate}
                                    </h5>
                                    {Object.keys(terminalsByDate).map((posTerminalName: string) => {
                                        const invoiceConclusionDetail = terminalsByDate[posTerminalName];
                                        const pluDataForDate = pluSource?.[shiftDateTimestamp];
                                        const invoiceConclusionDetailsByPlu = pluDataForDate?.[posTerminalName] || null;

                                        if (
                                            invoiceConclusionDetail.invoiced &&
                                            invoiceConclusionDetail.invoiced.length > 0
                                        ) {
                                            return (
                                                <div
                                                    className="mr-16p ml-16p"
                                                    key={`${shiftDateTimestamp}-${posTerminalName}`}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <h3>{posTerminalName}</h3>
                                                        {shouldPrint ? (
                                                            <div className="mb-8p">
                                                                {month ? (
                                                                    <span>
                                                                        {String(month).padStart(2, '0')}/{year}
                                                                    </span>
                                                                ) : (
                                                                    <span>
                                                                        {dayjs(from ?? fromRange ?? new Date()).format(
                                                                            otherSettings?.dateFormat ?? 'DD.MM.YYYY.'
                                                                        )}{' '}
                                                                        -{' '}
                                                                        {dayjs(to ?? toRange ?? new Date()).format(
                                                                            otherSettings?.dateFormat ?? 'DD.MM.YYYY.'
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </div>
                                                    <div>
                                                        <InvoiceConclusionDetailsHeader />
                                                        <InvoiceConclusionDetailsList
                                                            invoiceConclusionDetails={invoiceConclusionDetail?.invoiced}
                                                            defaultCurrency={defaultCurrency}
                                                            invoiceConclusionDetailsByPlu={
                                                                invoiceConclusionDetailsByPlu?.invoiced
                                                            }
                                                            setInvoiceConclusionsDetailsByPlu={
                                                                setInvoiceConclusionDetailsByPluByShiftDate
                                                            }
                                                            setSelectedTypeOrSubType={setSelectedTypeOrSubType}
                                                            selectedTypeOrSubType={selectedTypeOrSubType}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            return null;
                                        }
                                    })}
                                </div>
                            );
                        })
                ) : (searchTerm.trim() ? filteredByPosTerminalId.type : invoiceConclusionDetailsByPosTerminalId) ? (
                    // Default view - group by POS terminal
                    Object.keys(
                        searchTerm.trim() ? filteredByPosTerminalId.type! : invoiceConclusionDetailsByPosTerminalId!
                    ).map((posTerminalName: string) => {
                        const dataSource = searchTerm.trim()
                            ? filteredByPosTerminalId.type!
                            : invoiceConclusionDetailsByPosTerminalId!;
                        const pluSource = searchTerm.trim()
                            ? filteredByPosTerminalId.plu
                            : invoiceConclusionDetailsByPluByPosTerminalId;
                        const invoiceConclusionDetail = dataSource[posTerminalName];

                        const invoiceConclusionDetailsByPlu = pluSource
                            ? pluSource?.[posTerminalName]
                                ? pluSource[posTerminalName]
                                : null
                            : null;
                        if (invoiceConclusionDetail.invoiced && invoiceConclusionDetail.invoiced.length > 0) {
                            return (
                                <div className="mr-16p" key={posTerminalName}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <h3>{posTerminalName}</h3>
                                        {shouldPrint ? (
                                            <div className="mb-8p ml-8p">
                                                {month ? (
                                                    <span>
                                                        {String(month).padStart(2, '0')}/{year}
                                                    </span>
                                                ) : (
                                                    <span>
                                                        {dayjs(from ?? fromRange ?? new Date()).format(
                                                            otherSettings?.dateFormat ?? 'DD.MM.YYYY.'
                                                        )}{' '}
                                                        -{' '}
                                                        {dayjs(to ?? toRange ?? new Date()).format(
                                                            otherSettings?.dateFormat ?? 'DD.MM.YYYY.'
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                    <div>
                                        <InvoiceConclusionDetailsHeader />
                                        <InvoiceConclusionDetailsList
                                            invoiceConclusionDetails={invoiceConclusionDetail?.invoiced}
                                            defaultCurrency={defaultCurrency}
                                            invoiceConclusionDetailsByPlu={invoiceConclusionDetailsByPlu?.invoiced}
                                            setInvoiceConclusionsDetailsByPlu={
                                                setInvoiceConclusionsDetailsByPluByPosTerminalId
                                            }
                                            setSelectedTypeOrSubType={setSelectedTypeOrSubType}
                                            selectedTypeOrSubType={selectedTypeOrSubType}
                                        />
                                    </div>
                                </div>
                            );
                        } else {
                            return null;
                        }
                    })
                ) : (
                    <div className="text-center">{translate('There is no data for selected date(s).')}</div>
                )}
            </div>
        </div>
    );
};

export default FinancialJournal;
